import apollo from '~/lib/apollo';

import { Search } from '../hooks/useTargetMarket';
import CreateTargetMarketQuery from '../queries/CreateTargetMarket.graphql';
import DestroyTargetMarketQuery from '../queries/DestroyTargetMarket.graphql';
import UpdateTargetMarketQuery from '../queries/UpdateTargetMarket.graphql';
import { TargetMarketsCompany } from '../types';

export interface TargetMarket {
  id: string;
  name: string;
  search: Search;
}

export const createTargetMarket = async ({
  name,
  search,
}: Omit<TargetMarket, 'id'>): Promise<TargetMarket> => {
  const { data, errors } = await apollo.mutate({
    mutation: CreateTargetMarketQuery,
    variables: {
      input: { name, search },
    },
  });

  if (errors?.length) {
    throw new Error('Error creating target market');
  }

  return data;
};

export const updateTargetMarket = async ({
  id,
  search,
}: TargetMarket): Promise<TargetMarket> => {
  const { data, errors } = await apollo.mutate({
    mutation: UpdateTargetMarketQuery,
    variables: { input: { id, search } },
  });

  if (errors?.length) {
    throw new Error('Error updating target market');
  }

  return data;
};

export const destroyTargetMarket = async (id: string) => {
  const { data, errors } = await apollo.mutate({
    mutation: DestroyTargetMarketQuery,
    variables: { input: { id } },
  });

  if (errors?.length) {
    throw new Error('Error destroying target market');
  }

  return data;
};

export const SAMPLE_COMPANIES: TargetMarketsCompany[] = [
  {
    id: 'google.com',
    domain: 'google.com',
    name: 'Google',
    industry: 'Technology',
    employees_range: '10K-50K',
    country: 'United States',
    linkedin: 'company/google',
    twitter: 'google',
    crunchbase: 'organization/google',
  },
  {
    id: 'hubspot.com',
    domain: 'hubspot.com',
    name: 'HubSpot',
    industry: 'Technology',
    employees_range: '5K-10K',
    country: 'United States',
    linkedin: 'company/hubspot',
    twitter: 'hubspot',
    crunchbase: 'organization/hubspot',
  },
  {
    id: 'github.com',
    domain: 'github.com',
    name: 'GitHub',
    industry: 'Technology',
    employees_range: '10K-50K',
    country: 'United States',
    linkedin: 'company/github',
    twitter: 'github',
    crunchbase: 'organization/github',
  },
  {
    id: 'clearbit.com',
    domain: 'clearbit.com',
    name: 'Clearbit',
    industry: 'Technology',
    employees_range: '251-1K',
    country: 'United States',
    linkedin: 'company/clearbit',
    twitter: 'clearbit',
    crunchbase: 'organization/clearbit',
  },
  {
    id: 'ibm.com',
    domain: 'ibm.com',
    name: 'IBM',
    industry: 'Technology',
    employees_range: '10K-50K',
    country: 'United States',
    linkedin: 'company/ibm',
    twitter: 'ibm',
    crunchbase: 'organization/ibm',
  },
  {
    id: 'apple.com',
    domain: 'apple.com',
    name: 'Apple',
    industry: 'Technology',
    employees_range: '10K-50K',
    country: 'United States',
    linkedin: 'company/apple',
  },
  {
    id: 'microsoft.com',
    domain: 'microsoft.com',
    name: 'Microsoft',
    industry: 'Technology',
    employees_range: '10K-50K',
    country: 'United States',
    linkedin: 'company/microsoft',
    twitter: 'microsoft',
    crunchbase: 'organization/microsoft',
  },
  {
    id: 'meta.com',
    domain: 'meta.com',
    name: 'Meta',
    industry: 'Technology',
    employees_range: '10K-50K',
    country: 'United States',
    linkedin: 'company/meta',
  },
  {
    id: 'twitter.com',
    domain: 'twitter.com',
    name: 'Twitter',
    industry: 'Technology',
    employees_range: '10K-50K',
    country: 'United States',
    linkedin: 'company/twitter',
    twitter: 'twitter',
    crunchbase: 'organization/twitter',
  },
  {
    id: 'linkedin.com',
    domain: 'linkedin.com',
    name: 'LinkedIn',
    industry: 'Technology',
    employees_range: '251-1K',
    country: 'United States',
    linkedin: 'company/linkedin',
    twitter: 'linkedin',
    crunchbase: 'organization/linkedin',
  },
  {
    id: 'amazon.com',
    domain: 'amazon.com',
    name: 'Amazon',
    industry: 'Technology',
    employees_range: '10K-50K',
    country: 'United States',
    linkedin: 'company/amazon',
    twitter: 'amazon',
  },
  {
    id: 'netflix.com',
    domain: 'netflix.com',
    name: 'Netflix',
    industry: 'Technology',
    employees_range: '251-1K',
    country: 'United States',
    linkedin: 'company/netflix',
    twitter: 'netflix',
  },
  {
    id: 'zoom.us',
    domain: 'zoom.us',
    name: 'Zoom',
    industry: 'Technology',
    employees_range: '251-1K',
    country: 'United States',
    linkedin: 'company/zoom-video-communications',
  },
  {
    id: 'salesforce.com',
    domain: 'salesforce.com',
    name: 'Salesforce',
    industry: 'Technology',
    employees_range: '10K-50K',
    country: 'United States',
    linkedin: 'company/salesforce',
    twitter: 'salesforce',
  },
  {
    id: 'stripe.com',
    domain: 'stripe.com',
    name: 'Stripe',
    industry: 'Technology',
    employees_range: '251-1K',
    country: 'United States',
    linkedin: 'company/stripe',
    twitter: 'stripe',
    crunchbase: 'organization/stripe',
  },
];
